import React from 'react';
import { FiFilter, FiSearch } from 'react-icons/fi';
import { IconType } from 'react-icons';
import {
  STALE_INACTIVE_DAYS,
  USER_TYPE_FILTER_OPTIONS,
} from './buildRoadmapListParams';

const ROADMAP_SOURCE_FILTER_OPTIONS = [
  { value: 'all', label: 'Tüm kayıtlar' },
  { value: 'yes', label: "Sadece Roadmap: Evet" },
  { value: 'no', label: "Sadece Roadmap: Hayır" },
];

type RoadmapFilterDropdownOption = { value: string; label: string };

type RoadmapFilterDropdownProps = {
  label: string;
  labelId: string;
  value: string;
  onChange: (value: string) => void;
  options: RoadmapFilterDropdownOption[];
  placeholder: string;
};

function RoadmapFilterDropdown({
  label,
  labelId,
  value,
  onChange,
  options,
  placeholder,
}: RoadmapFilterDropdownProps) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const displayText = selected?.label ?? placeholder;

  return (
    <div className="space-y-1.5" ref={wrapRef}>
      <span
        id={`${labelId}-caption`}
        className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
      >
        {label}
      </span>
      <div className="relative">
        <button
          type="button"
          id={labelId}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="flex h-[42px] w-full items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3.5 text-left text-sm shadow-sm dark:border-slate-600 dark:bg-gray-800 dark:text-slate-100"
        >
          <span className="min-w-0 flex-1 truncate font-medium">{displayText}</span>
          <span className="text-slate-400">▾</span>
        </button>
        {open && (
          <ul
            role="listbox"
            className="absolute left-0 right-0 top-[calc(100%+6px)] z-[60] max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-600 dark:bg-gray-900"
          >
            {options.map((opt) => (
              <li key={opt.value || '__empty'} role="presentation">
                <button
                  type="button"
                  role="option"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className="flex w-full px-3 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-gray-800"
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export type RoadmapApiFiltersPanelProps = {
  apiFilterPlatform: string;
  setApiFilterPlatform: (v: string) => void;
  apiFilterRoadmap: 'all' | 'yes' | 'no';
  setApiFilterRoadmap: (v: 'all' | 'yes' | 'no') => void;
  apiFilterMinOrigin: string;
  setApiFilterMinOrigin: (v: string) => void;
  apiFilterDateFrom: string;
  setApiFilterDateFrom: (v: string) => void;
  apiFilterDateTo: string;
  setApiFilterDateTo: (v: string) => void;
  apiFilterStaleOnly: boolean;
  setApiFilterStaleOnly: (v: boolean) => void;
  apiFilterUserType: string;
  setApiFilterUserType: (v: string) => void;
  apiPlayerSearchInput: string;
  setApiPlayerSearchInput: (v: string) => void;
  apiFilterPlayerId: string;
  apiFiltersActive: boolean;
  loading: boolean;
  pagination: { total: number; totalPages: number };
  currentPage: number;
  onPageOne: () => void;
  onReset: () => void;
  onSubmitPlayerSearch: (e?: React.FormEvent) => void;
  onClearPlayerSearch: () => void;
  renderIcon: (Icon: IconType, className: string) => React.ReactNode;
};

export function RoadmapApiFiltersPanel(props: RoadmapApiFiltersPanelProps) {
  const {
    apiFilterPlatform,
    setApiFilterPlatform,
    apiFilterRoadmap,
    setApiFilterRoadmap,
    apiFilterMinOrigin,
    setApiFilterMinOrigin,
    apiFilterDateFrom,
    setApiFilterDateFrom,
    apiFilterDateTo,
    setApiFilterDateTo,
    apiFilterStaleOnly,
    setApiFilterStaleOnly,
    apiFilterUserType,
    setApiFilterUserType,
    apiPlayerSearchInput,
    setApiPlayerSearchInput,
    apiFilterPlayerId,
    apiFiltersActive,
    loading,
    pagination,
    currentPage,
    onPageOne,
    onReset,
    onSubmitPlayerSearch,
    onClearPlayerSearch,
    renderIcon,
  } = props;

  return (
    <div className="space-y-5">
      <form onSubmit={onSubmitPlayerSearch} className="flex flex-col gap-1.5 sm:max-w-xl">
        <label
          htmlFor="api-player-search"
          className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
        >
          Player ID ara
        </label>
        <div className="flex flex-wrap gap-2">
          <input
            id="api-player-search"
            type="search"
            autoComplete="off"
            value={apiPlayerSearchInput}
            onChange={(e) => setApiPlayerSearchInput(e.target.value)}
            placeholder="Tam eşleşme (UUID)"
            className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-mono text-slate-800 shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 dark:border-slate-600 dark:bg-gray-800 dark:text-slate-100 dark:placeholder:text-slate-500"
          />
          <button
            type="submit"
            disabled={loading || !apiPlayerSearchInput.trim()}
            className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {renderIcon(FiSearch, 'h-4 w-4')}
            Ara
          </button>
          {apiFilterPlayerId ? (
            <button
              type="button"
              onClick={onClearPlayerSearch}
              disabled={loading}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
            >
              Aramayı temizle
            </button>
          ) : null}
        </div>
      </form>

      <div className="rounded-2xl border border-slate-200/90 dark:border-slate-600/80 bg-gradient-to-br from-white via-slate-50/95 to-indigo-50/25 dark:from-gray-900 dark:to-indigo-950/25 shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-200/80 dark:border-slate-700/80">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white">
              {renderIcon(FiFilter, 'h-5 w-5')}
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-800 dark:text-white">Filtreler</h4>
              <p className="text-xs text-slate-500 dark:text-slate-400">API — /analytics/dashboard/roadmap/</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onReset}
            disabled={!apiFiltersActive || loading}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40 dark:border-slate-600 dark:bg-gray-800 dark:text-slate-300 dark:hover:bg-gray-700"
          >
            Tümünü sıfırla
          </button>
        </div>
        <div className="p-5 sm:p-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <label htmlFor="api-filter-platform" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Platform
              </label>
              <input
                id="api-filter-platform"
                type="text"
                value={apiFilterPlatform}
                onChange={(e) => {
                  setApiFilterPlatform(e.target.value);
                  onPageOne();
                }}
                placeholder="ör. iOS"
                className="block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 dark:border-slate-600 dark:bg-gray-800 dark:text-slate-100 dark:placeholder:text-slate-500"
              />
            </div>
            <RoadmapFilterDropdown
              label={"Roadmap'ten"}
              labelId="api-filter-roadmap"
              value={apiFilterRoadmap}
              onChange={(v) => {
                setApiFilterRoadmap(v as 'all' | 'yes' | 'no');
                onPageOne();
              }}
              placeholder="Tüm kayıtlar"
              options={ROADMAP_SOURCE_FILTER_OPTIONS}
            />
            <RoadmapFilterDropdown
              label="Kullanıcı tipi"
              labelId="api-filter-user-type"
              value={apiFilterUserType}
              onChange={(v) => {
                setApiFilterUserType(v);
                onPageOne();
              }}
              placeholder="Tüm kullanıcı tipleri"
              options={[...USER_TYPE_FILTER_OPTIONS]}
            />
            <div className="space-y-1.5">
              <label htmlFor="api-filter-origin" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Min. origin (≥)
              </label>
              <input
                id="api-filter-origin"
                type="number"
                min={0}
                value={apiFilterMinOrigin}
                onChange={(e) => {
                  setApiFilterMinOrigin(e.target.value);
                  onPageOne();
                }}
                placeholder="ör. 61"
                className="block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 dark:border-slate-600 dark:bg-gray-800 dark:text-slate-100 dark:placeholder:text-slate-500"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="api-filter-date-from" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                from_date
              </label>
              <input
                id="api-filter-date-from"
                type="date"
                value={apiFilterDateFrom}
                onChange={(e) => {
                  setApiFilterDateFrom(e.target.value);
                  onPageOne();
                }}
                className="block h-[42px] w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 dark:border-slate-600 dark:bg-gray-800 dark:text-slate-100 [color-scheme:light] dark:[color-scheme:dark]"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="api-filter-date-to" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                upto_date
              </label>
              <input
                id="api-filter-date-to"
                type="date"
                value={apiFilterDateTo}
                onChange={(e) => {
                  setApiFilterDateTo(e.target.value);
                  onPageOne();
                }}
                className="block h-[42px] w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 dark:border-slate-600 dark:bg-gray-800 dark:text-slate-100 [color-scheme:light] dark:[color-scheme:dark]"
              />
            </div>
          </div>
          <div className="mt-5 flex items-start gap-3 rounded-xl border border-rose-200/90 bg-rose-50/50 px-4 py-3.5 dark:border-rose-900/45 dark:bg-rose-950/20">
            <input
              id="api-filter-stale-only"
              type="checkbox"
              checked={apiFilterStaleOnly}
              onChange={(e) => {
                setApiFilterStaleOnly(e.target.checked);
                onPageOne();
              }}
              className="mt-0.5 h-4 w-4 rounded"
            />
            <label htmlFor="api-filter-stale-only" className="cursor-pointer text-sm text-rose-950 dark:text-rose-50">
              <span className="font-semibold">Sadece uyarılı oyuncular</span>
              <span className="mt-1 block text-xs text-rose-900/85 dark:text-rose-200/90">
                last_activity_n_days_ago = {STALE_INACTIVE_DAYS}
              </span>
            </label>
          </div>
        </div>
      </div>

      {pagination.total > 0 && (
        <p className="text-sm text-slate-600 dark:text-slate-400">
          <strong className="text-slate-800 dark:text-slate-200">{pagination.total}</strong> eşleşme
          {pagination.totalPages > 1 && (
            <>
              {' '}
              · sayfa <strong className="text-slate-800 dark:text-slate-200">{currentPage}</strong> /{' '}
              <strong className="text-slate-800 dark:text-slate-200">{pagination.totalPages}</strong>
            </>
          )}
        </p>
      )}
    </div>
  );
}
